import { __rest } from "tslib";
import * as React from 'react';
import classNames from 'classnames';
import { TreeContext } from './contextTypes';
import { getDataAndAria } from './util';
import Indent from './Indent';
import { convertNodePropsToEventData } from './utils/treeUtil';
const ICON_OPEN = 'open';
const ICON_CLOSE = 'close';
const defaultTitle = '---';
class InternalTreeNode extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            dragNodeHighlight: false,
        };
        this.onSelectorClick = (e) => {
            const { context: { onNodeClick }, } = this.props;
            onNodeClick(e, convertNodePropsToEventData(this.props));
            if (this.isSelectable()) {
                this.onSelect(e);
            }
            else {
                this.onCheck(e);
            }
        };
        this.onSelectorDoubleClick = (e) => {
            const { context: { onNodeDoubleClick }, } = this.props;
            onNodeDoubleClick(e, convertNodePropsToEventData(this.props));
        };
        this.onSelect = (e) => {
            if (this.isDisabled())
                return;
            const { context: { onNodeSelect }, } = this.props;
            e.preventDefault();
            onNodeSelect(e, convertNodePropsToEventData(this.props));
        };
        this.onCheck = (e) => {
            if (this.isDisabled())
                return;
            const { disableCheckbox, checked } = this.props;
            const { context: { onNodeCheck }, } = this.props;
            if (!this.isCheckable() || disableCheckbox)
                return;
            e.preventDefault();
            const targetChecked = !checked;
            onNodeCheck(e, convertNodePropsToEventData(this.props), targetChecked);
        };
        this.onMouseEnter = (e) => {
            const { context: { onNodeMouseEnter }, } = this.props;
            onNodeMouseEnter(e, convertNodePropsToEventData(this.props));
        };
        this.onMouseLeave = (e) => {
            const { context: { onNodeMouseLeave }, } = this.props;
            onNodeMouseLeave(e, convertNodePropsToEventData(this.props));
        };
        this.onContextMenu = (e) => {
            const { context: { onNodeContextMenu }, } = this.props;
            onNodeContextMenu(e, convertNodePropsToEventData(this.props));
        };
        this.onDragStart = (e) => {
            const { context: { onNodeDragStart }, } = this.props;
            e.stopPropagation();
            onNodeDragStart(e, this);
            this.setState({
                dragNodeHighlight: true,
            });
            try {
                e.dataTransfer.setData('text/plain', '');
                const offsetWidth = e.currentTarget.clientWidth >> (1 + 20);
                const offsetHeight = e.currentTarget.clientHeight;
                e.dataTransfer.setDragImage(e.currentTarget, -offsetWidth, -5);
            }
            catch (error) {
            }
        };
        this.onDragEnter = (e) => {
            const { context: { onNodeDragEnter }, } = this.props;
            e.preventDefault();
            e.stopPropagation();
            onNodeDragEnter(e, this);
        };
        this.onDragOver = (e) => {
            const { context: { onNodeDragOver }, } = this.props;
            e.preventDefault();
            e.stopPropagation();
            onNodeDragOver(e, this);
        };
        this.onDragLeave = (e) => {
            const { context: { onNodeDragLeave }, } = this.props;
            e.stopPropagation();
            onNodeDragLeave(e, this);
        };
        this.onDragEnd = (e) => {
            const { context: { onNodeDragEnd }, } = this.props;
            e.stopPropagation();
            this.setState({
                dragNodeHighlight: false,
            });
            onNodeDragEnd(e, this);
        };
        this.onDrop = (e) => {
            const { context: { onNodeDrop }, } = this.props;
            e.preventDefault();
            this.setState({
                dragNodeHighlight: false,
            });
            onNodeDrop(e, this);
        };
        this.onExpand = e => {
            const { loading, context: { onNodeExpand }, } = this.props;
            if (loading)
                return;
            onNodeExpand(e, convertNodePropsToEventData(this.props));
        };
        this.setSelectHandle = node => {
            this.selectHandle = node;
        };
        this.getNodeState = () => {
            const { expanded } = this.props;
            if (this.isLeaf()) {
                return null;
            }
            return expanded ? ICON_OPEN : ICON_CLOSE;
        };
        this.hasChildren = () => {
            const { eventKey } = this.props;
            const { context: { keyEntities }, } = this.props;
            const { children } = keyEntities[eventKey] || {};
            return !!(children || []).length;
        };
        this.isLeaf = () => {
            const { isLeaf, loaded } = this.props;
            const { context: { loadData }, } = this.props;
            const hasChildren = this.hasChildren();
            if (isLeaf === false) {
                return false;
            }
            return isLeaf || (!loadData && !hasChildren) || (loadData && loaded && !hasChildren);
        };
        this.isDisabled = () => {
            const { disabled } = this.props;
            const { context: { disabled: treeDisabled }, } = this.props;
            return !!(treeDisabled || disabled);
        };
        this.isCheckable = () => {
            const { checkable } = this.props;
            const { context: { checkable: treeCheckable }, } = this.props;
            if (!treeCheckable || checkable === false)
                return false;
            return treeCheckable;
        };
        this.syncLoadData = props => {
            const { expanded, loading, loaded } = props;
            const { context: { loadData, onNodeLoad }, } = this.props;
            if (loading) {
                return;
            }
            if (loadData && expanded && !this.isLeaf()) {
                if (!this.hasChildren() && !loaded) {
                    onNodeLoad(convertNodePropsToEventData(this.props));
                }
            }
        };
        this.renderSwitcherIconDom = (isLeaf) => {
            const { switcherIcon: switcherIconFromProps } = this.props;
            const { context: { switcherIcon: switcherIconFromCtx }, } = this.props;
            const switcherIcon = switcherIconFromProps || switcherIconFromCtx;
            if (typeof switcherIcon === 'function') {
                return switcherIcon(Object.assign(Object.assign({}, this.props), { isLeaf }));
            }
            return switcherIcon;
        };
        this.renderSwitcher = () => {
            const { expanded } = this.props;
            const { context: { prefixCls }, } = this.props;
            if (this.isLeaf()) {
                const switcherIconDom = this.renderSwitcherIconDom(true);
                return switcherIconDom !== false ? React.createElement("span", { className: classNames(`${prefixCls}-switcher`, `${prefixCls}-switcher-noop`) }, switcherIconDom) : null;
            }
            const switcherCls = classNames(`${prefixCls}-switcher`, `${prefixCls}-switcher_${expanded ? ICON_OPEN : ICON_CLOSE}`);
            const switcherIconDom = this.renderSwitcherIconDom(false);
            return switcherIconDom !== false ? (React.createElement("span", { onClick: this.onExpand, className: switcherCls }, switcherIconDom)) : null;
        };
        this.renderCheckbox = () => {
            const { checked, halfChecked, disableCheckbox } = this.props;
            const { context: { prefixCls }, } = this.props;
            const disabled = this.isDisabled();
            const checkable = this.isCheckable();
            if (!checkable)
                return null;
            const $custom = typeof checkable !== 'boolean' ? checkable : null;
            return (React.createElement("span", { className: classNames(`${prefixCls}-checkbox`, checked && `${prefixCls}-checkbox-checked`, !checked && halfChecked && `${prefixCls}-checkbox-indeterminate`, (disabled || disableCheckbox) && `${prefixCls}-checkbox-disabled`), onClick: this.onCheck }, $custom));
        };
        this.renderIcon = () => {
            const { loading } = this.props;
            const { context: { prefixCls }, } = this.props;
            return React.createElement("span", { className: classNames(`${prefixCls}-iconEle`, `${prefixCls}-icon__${this.getNodeState() || 'docu'}`, loading && `${prefixCls}-icon_loading`) });
        };
        this.renderSelector = () => {
            const { dragNodeHighlight } = this.state;
            const { title, selected, icon, loading, data } = this.props;
            const { context: { prefixCls, showIcon, icon: treeIcon, draggable, loadData, titleRender }, } = this.props;
            const disabled = this.isDisabled();
            const mergedDraggable = typeof draggable === 'function' ? draggable(data) : draggable;
            const wrapClass = `${prefixCls}-node-content-wrapper`;
            let $icon;
            if (showIcon) {
                const currentIcon = icon || treeIcon;
                $icon = currentIcon ? (React.createElement("span", { className: classNames(`${prefixCls}-iconEle`, `${prefixCls}-icon__customize`) }, typeof currentIcon === 'function' ? currentIcon(this.props) : currentIcon)) : (this.renderIcon());
            }
            else if (loadData && loading) {
                $icon = this.renderIcon();
            }
            let titleNode;
            if (typeof title === 'function') {
                titleNode = title(data);
            }
            else if (titleRender) {
                titleNode = titleRender(data);
            }
            else {
                titleNode = title;
            }
            const $title = React.createElement("span", { className: `${prefixCls}-title` }, titleNode);
            return (React.createElement("span", { ref: this.setSelectHandle, title: typeof title === 'string' ? title : '', className: classNames(`${wrapClass}`, `${wrapClass}-${this.getNodeState() || 'normal'}`, !disabled && (selected || dragNodeHighlight) && `${prefixCls}-node-selected`, !disabled && mergedDraggable && 'draggable'), draggable: (!disabled && mergedDraggable) || undefined, "aria-grabbed": (!disabled && mergedDraggable) || undefined, onMouseEnter: this.onMouseEnter, onMouseLeave: this.onMouseLeave, onContextMenu: this.onContextMenu, onClick: this.onSelectorClick, onDoubleClick: this.onSelectorDoubleClick, onDragStart: mergedDraggable ? this.onDragStart : undefined },
                React.createElement("span", { onClick: (e) => {
                        e.stopPropagation();
                        this.onExpand(e);
                    } }, $icon),
                $title,
                this.renderDropIndicator()));
        };
        this.renderDropIndicator = () => {
            const { disabled, eventKey } = this.props;
            const { context: { dragModel, draggable, dropLevelOffset, dropPosition, prefixCls, indent, dropIndicatorRender, dragOverNodeKey, direction }, } = this.props;
            const mergedDraggable = draggable !== false;
            const isOuterDrag = dragModel == 'drag';
            const showIndicator = !disabled && mergedDraggable && dragOverNodeKey === eventKey;
            return showIndicator && !isOuterDrag
                ? dropIndicatorRender({
                    dropPosition,
                    dropLevelOffset,
                    indent,
                    prefixCls,
                    direction,
                })
                : null;
        };
    }
    componentDidMount() {
        this.syncLoadData(this.props);
    }
    componentDidUpdate() {
        this.syncLoadData(this.props);
    }
    isSelectable() {
        const { selectable } = this.props;
        const { context: { selectable: treeSelectable }, } = this.props;
        if (typeof selectable === 'boolean') {
            return selectable;
        }
        return treeSelectable;
    }
    render() {
        const _a = this.props, { eventKey, className, style, dragOver, dragOverGapTop, dragOverGapBottom, isLeaf, isStart, isEnd, expanded, selected, checked, halfChecked, loading, domRef, active, data, onMouseMove } = _a, otherProps = __rest(_a, ["eventKey", "className", "style", "dragOver", "dragOverGapTop", "dragOverGapBottom", "isLeaf", "isStart", "isEnd", "expanded", "selected", "checked", "halfChecked", "loading", "domRef", "active", "data", "onMouseMove"]);
        const { context: { dragModel, prefixCls, filterTreeNode, draggable, keyEntities, dropContainerKey, dropTargetKey }, } = this.props;
        const isOuterDrag = dragModel == 'drag';
        const disabled = this.isDisabled();
        const dataOrAriaAttributeProps = getDataAndAria(otherProps);
        const { level } = keyEntities[eventKey] || {};
        const isEndNode = isEnd[isEnd.length - 1];
        const mergedDraggable = (typeof draggable === 'function' ? draggable(data) : draggable) || isOuterDrag;
        return (React.createElement("div", Object.assign({ ref: domRef, className: classNames(className, `${prefixCls}-treenode`, {
                [`${prefixCls}-treenode-disabled`]: disabled,
                [`${prefixCls}-treenode-switcher-${expanded ? 'open' : 'close'}`]: !isLeaf,
                [`${prefixCls}-treenode-checkbox-checked`]: checked,
                [`${prefixCls}-treenode-checkbox-indeterminate`]: halfChecked,
                [`${prefixCls}-treenode-selected`]: selected,
                [`${prefixCls}-treenode-loading`]: loading,
                [`${prefixCls}-treenode-active`]: active,
                [`${prefixCls}-treenode-leaf-last`]: isEndNode,
                'drop-target': dropTargetKey === eventKey && !isOuterDrag,
                'drop-container': dropContainerKey === eventKey && !isOuterDrag,
                'drag-over': !disabled && dragOver && !isOuterDrag,
                'drag-over-gap-top': !disabled && dragOverGapTop && !isOuterDrag,
                'drag-over-gap-bottom': !disabled && dragOverGapBottom && !isOuterDrag,
                'filter-node': filterTreeNode && filterTreeNode(convertNodePropsToEventData(this.props)),
                'drag-outer-over': !disabled && (dragOver || dragOverGapTop || dragOverGapBottom || dropContainerKey === eventKey) && isOuterDrag,
            }), style: style, onDragEnter: mergedDraggable ? this.onDragEnter : undefined, onDragOver: mergedDraggable ? this.onDragOver : undefined, onDragLeave: mergedDraggable ? this.onDragLeave : undefined, onDrop: mergedDraggable ? this.onDrop : undefined, onDragEnd: mergedDraggable ? this.onDragEnd : undefined, onMouseMove: onMouseMove }, dataOrAriaAttributeProps),
            React.createElement(Indent, { prefixCls: prefixCls, level: level, isStart: isStart, isEnd: isEnd }),
            this.renderSwitcher(),
            this.renderCheckbox(),
            this.renderSelector()));
    }
}
const ContextTreeNode = props => React.createElement(TreeContext.Consumer, null, context => React.createElement(InternalTreeNode, Object.assign({}, props, { context: context })));
ContextTreeNode.displayName = 'TreeNode';
ContextTreeNode.defaultProps = {
    title: defaultTitle,
};
ContextTreeNode.isTreeNode = 1;
export { InternalTreeNode };
export default ContextTreeNode;
